import { Button, Css, Icon, Palette, TextField, useTestIds } from "@homebound/beam";
import { observer } from "mobx-react";
import { useCallback, useMemo, useState } from "react";
import { buildTextureScoringContext, scoreVariantForTexture } from "../hooks/useFuzzyMatch";
import { useInsideMapsConfig } from "../hooks/useInsideMapsConfig";
import { MaterialSelectionMode, MaterialVariantOption } from "../types";

interface MaterialSelectorProps {
  tliMaterialVariants: MaterialVariantOption[];
  dpMaterialVariants: MaterialVariantOption[];
  allMaterialVariants: MaterialVariantOption[];
}

export const MaterialSelector = observer(function MaterialSelector({
  tliMaterialVariants,
  dpMaterialVariants,
  allMaterialVariants,
}: MaterialSelectorProps) {
  const store = useInsideMapsConfig();
  const tid = useTestIds({});
  const [mode, setMode] = useState<MaterialSelectionMode>("design-package");
  const [search, setSearch] = useState("");
  const [hardcodedIds, setHardcodedIds] = useState("");

  const part = store.selectedPart;
  const texture = store.selectedTexture;
  const cameraId = store.selectedCameraId;
  const partId = store.selectedPartId;
  const textureIndex = store.selectedTextureIndex;

  const scoringContext = useMemo(() => {
    if (!part || textureIndex === null) return null;
    return buildTextureScoringContext(part, textureIndex);
  }, [part, textureIndex]);

  const sourceVariants = mode === "design-package" ? dpMaterialVariants : tliMaterialVariants;

  const scoredAndFilteredVariants = useMemo(() => {
    const scored = sourceVariants.map((mv) => ({ ...mv, score: scoreVariantForTexture(mv, scoringContext) }));

    let filtered = scored;
    if (search) {
      const tokens = search.toLowerCase().split(/\s+/).filter(Boolean);
      filtered = scored.filter((mv) => {
        const text = `${mv.displayName} ${mv.id} ${mv.brandName ?? ""}`.toLowerCase();
        return tokens.every((token) => text.includes(token));
      });
    }

    return filtered.sort((a, b) => b.score - a.score || a.displayName.localeCompare(b.displayName));
  }, [sourceVariants, scoringContext, search]);

  const handleToggleMaterial = useCallback(
    (mvId: string) => {
      if (!cameraId || !partId || textureIndex === null || !texture) return;
      const current = texture.materialVariantIds ?? [];
      const next = current.includes(mvId) ? current.filter((id) => id !== mvId) : [...current, mvId];
      store.setMaterialVariantIds(cameraId, partId, textureIndex, next);
    },
    [store, cameraId, partId, textureIndex, texture],
  );

  const handleRemoveMaterial = useCallback(
    (mvId: string) => {
      if (!cameraId || !partId || textureIndex === null || !texture) return;
      const current = texture.materialVariantIds ?? [];
      store.setMaterialVariantIds(
        cameraId,
        partId,
        textureIndex,
        current.filter((id) => id !== mvId),
      );
    },
    [store, cameraId, partId, textureIndex, texture],
  );

  const handleClearAllSelected = useCallback(() => {
    if (!cameraId || !partId || textureIndex === null) return;
    store.setMaterialVariantIds(cameraId, partId, textureIndex, []);
  }, [store, cameraId, partId, textureIndex]);

  const handleHardcodedSubmit = useCallback(() => {
    if (!cameraId || !partId || textureIndex === null) return;
    store.setHardcodedMaterialVariantIds(cameraId, partId, textureIndex, hardcodedIds);
    setHardcodedIds("");
  }, [store, cameraId, partId, textureIndex, hardcodedIds]);

  const handleCopyDown = useCallback(() => {
    if (!cameraId || !partId || textureIndex === null) return;
    store.copyMaterialsDown(cameraId, partId, textureIndex);
  }, [store, cameraId, partId, textureIndex]);

  if (!texture || !part) {
    return (
      <div css={Css.gray500.tac.mt4.$} {...tid.materialSelector}>
        Select a texture to see material options.
      </div>
    );
  }

  const selectedIds = texture.materialVariantIds ?? [];
  const selectedMaterials = selectedIds.map((id) => allMaterialVariants.find((mv) => mv.id === id)).compact();

  return (
    <div {...tid.materialSelector}>
      {/* Header */}
      <div css={Css.lg.add("fontWeight", "600").mb2.$}>Select Materials for: {texture.name}</div>

      {/* Material Source mode tabs */}
      <div css={Css.xs.gray600.mb1.$}>Material Source:</div>
      <div css={Css.df.gap1.mb3.$}>
        {(["design-package", "takeoff-line-items", "hardcoded"] as MaterialSelectionMode[]).map((m) => (
          <button
            key={m}
            css={
              Css.pyPx(6)
                .pxPx(14)
                .br4.sm.cursorPointer.if(mode === m)
                .white.add("background", "#1d4ed8")
                .add("border", "1px solid #1d4ed8")
                .if(mode !== m)
                .gray700.bgWhite.add("border", "1px solid #d1d5db").$
            }
            onClick={() => setMode(m)}
          >
            {m === "design-package"
              ? "Design Package"
              : m === "takeoff-line-items"
                ? "Takeoff Line Items"
                : "Hardcoded"}
          </button>
        ))}
      </div>

      {/* Selected Materials section */}
      {selectedMaterials.length > 0 && (
        <div css={Css.mb3.br8.ba.add("borderColor", "#bbf7d0").add("backgroundColor", "#f0fdf4").p2.$}>
          <div css={Css.df.aic.jcsb.mb2.$}>
            <span css={Css.smSb.green800.$}>Selected Materials ({selectedMaterials.length})</span>
            <button
              css={Css.xs.red600.cursorPointer.add("background", "none").add("border", "none").$}
              onClick={handleClearAllSelected}
            >
              Clear All
            </button>
          </div>
          {selectedMaterials.map((mv) => (
            <div key={mv.id} css={Css.df.aic.gap2.py1.px2.bgWhite.br8.mb1.$}>
              {mv.imageUrl ? (
                <img
                  src={mv.imageUrl}
                  alt={mv.displayName}
                  css={Css.wPx(48).hPx(48).br4.add("objectFit", "cover").add("flexShrink", "0").$}
                />
              ) : (
                <div css={Css.wPx(48).hPx(48).br4.bgGray200.df.aic.jcc.xs.gray400.add("flexShrink", "0").$}>img</div>
              )}
              <div css={Css.fg1.add("minWidth", "0").$}>
                <div css={Css.sm.smSb.$}>{mv.displayName}</div>
                {mv.costCodeNumber && (
                  <div css={Css.xs.gray500.$}>
                    Cost Code: {mv.costCodeNumber} {mv.costCodeDisplayName}
                  </div>
                )}
                {mv.brandName && <div css={Css.xs.gray500.$}>Brand: {mv.brandName}</div>}
                <div css={Css.xs.gray400.$}>ID: {mv.id}</div>
              </div>
              <button
                css={Css.cursorPointer.add("background", "none").add("border", "none").add("flexShrink", "0").$}
                onClick={() => handleRemoveMaterial(mv.id)}
              >
                <Icon icon="x" inc={2.5} color={Palette.Red400} />
              </button>
            </div>
          ))}
        </div>
      )}

      {mode === "hardcoded" ? (
        <div css={Css.df.fdc.gap1.$}>
          <TextField
            label="Material Variant IDs (comma-separated)"
            value={hardcodedIds}
            onChange={(val) => setHardcodedIds(val ?? "")}
            placeholder="mv:123, mv:456"
          />
          <Button label="Apply" onClick={handleHardcodedSubmit} />
        </div>
      ) : (
        <>
          {/* Search */}
          <TextField
            label="Search materials..."
            labelStyle="hidden"
            value={search}
            onChange={(val) => setSearch(val ?? "")}
            placeholder="Search materials..."
          />

          {/* Available Materials header */}
          <div css={Css.df.aic.jcsb.mt2.mb1.$}>
            <span css={Css.smSb.$}>Available Materials ({scoredAndFilteredVariants.length})</span>
            <Button label="Copy Down" variant="tertiary" size="sm" onClick={handleCopyDown} />
          </div>

          {/* Materials list */}
          <div css={Css.oa.add("maxHeight", "calc(100vh - 500px)").$}>
            {scoredAndFilteredVariants.map((mv) => {
              const isSelected = selectedIds.includes(mv.id);
              return (
                <MaterialVariantRow
                  key={mv.id}
                  mv={mv}
                  isSelected={isSelected}
                  onToggle={() => handleToggleMaterial(mv.id)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
});

function MaterialVariantRow({
  mv,
  isSelected,
  onToggle,
}: {
  mv: MaterialVariantOption & { score: number };
  isSelected: boolean;
  onToggle: () => void;
}) {
  const tid = useTestIds({});

  return (
    <div
      css={
        Css.df.aic.gap2.py2.px2.br8.cursorPointer.mb1.ba
          .if(isSelected)
          .add("borderColor", "#bbf7d0")
          .add("backgroundColor", "#f0fdf4")
          .if(!isSelected).bcGray200.bgWhite.$
      }
      onClick={onToggle}
      {...tid.materialVariantRow}
    >
      {/* Thumbnail */}
      {mv.imageUrl ? (
        <img
          src={mv.imageUrl}
          alt={mv.displayName}
          css={Css.wPx(48).hPx(48).br4.add("objectFit", "cover").add("flexShrink", "0").$}
        />
      ) : (
        <div css={Css.wPx(48).hPx(48).br4.bgGray200.df.aic.jcc.xs.gray400.add("flexShrink", "0").$}>img</div>
      )}

      {/* Material info */}
      <div css={Css.fg1.add("minWidth", "0").$}>
        <div css={Css.sm.smSb.$}>{mv.displayName}</div>
        {mv.costCodeNumber && (
          <div css={Css.xs.gray500.$}>
            Cost Code: {mv.costCodeNumber} {mv.costCodeDisplayName}
          </div>
        )}
        {mv.brandName && <div css={Css.xs.gray500.$}>Brand: {mv.brandName}</div>}
        <div css={Css.xs.gray400.$}>ID: {mv.id}</div>
      </div>

      {/* Score */}
      {mv.score > 0 && (
        <div css={Css.df.fdc.aic.add("flexShrink", "0").$}>
          <span css={Css.smSb.$}>{Math.round(mv.score * 100)}</span>
          <span css={Css.xs.gray500.$}>Score</span>
        </div>
      )}
    </div>
  );
}
